import { isNumber, uid } from 'radash';

import { PortHelpers } from '@diagram/helpers/PortHelpers';
import { ComponentInstanceService } from '@diagram/services/ComponentInstanceService';

import {
    ComponentQuery,
    Diagram,
    DiagramComponentInstance,
    DiagramConnection,
    DiagramConnectionAnchor,
    Project,
    VoltageClass,
    VoltageType,
} from 'models';
import { FormatHelpers } from 'helpers/formatters';

import { MeasurementSystemService } from 'services/MeasurementSystemService';

const ConnectionHelpers = {
    __validateHelperArguments: (
        connection: DiagramConnection,
        fromComponentInstance: DiagramComponentInstance,
        toComponentInstance: DiagramComponentInstance,
    ) => {
        if (
            connection.from.componentInstanceId !== fromComponentInstance.id ||
            connection.to.componentInstanceId !== toComponentInstance.id
        ) {
            console.error('ConnectionHelpers.mismatch', {
                connection,
                fromComponentInstance,
                toComponentInstance,
            });

            throw new Error(
                "Bro, did you mix your from & to components? (components don't match connection endpoints)",
            );
        }
    },

    generateId: () => {
        return `connection-${uid(8)}`;
    },

    generatePrettyId: (connection: DiagramConnection) => {
        let hash = 216613626139583;

        for (let i = 0; i < connection.id.length; i++) {
            hash ^= connection.id.charCodeAt(i);
            hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
        }

        return (Math.abs(hash) % 100_000).toString();
    },

    getAvailableVoltageTypes: (
        connection: DiagramConnection,
        fromComponentInstance: DiagramComponentInstance,
        toComponentInstance: DiagramComponentInstance,
    ): VoltageType[] => {
        ConnectionHelpers.__validateHelperArguments(connection, fromComponentInstance, toComponentInstance);

        const { from, to } = connection;

        let fromVoltageTypes: VoltageType[] = ['AC', 'DC'];
        let toVoltageTypes: VoltageType[] = ['AC', 'DC'];

        if (isNumber(from.port)) {
            fromVoltageTypes = PortHelpers.getVoltageTypeOptions(fromComponentInstance, from.port);
        }

        if (isNumber(to.port)) {
            toVoltageTypes = PortHelpers.getVoltageTypeOptions(toComponentInstance, to.port);
        }

        return fromVoltageTypes.filter((value) => toVoltageTypes.includes(value));
    },

    getEndpointOrder: (
        fromComponentInstance: DiagramComponentInstance,
        toComponentInstance: DiagramComponentInstance,
    ):
        | {
              start: 'from';
              end: 'to';
          }
        | {
              start: 'to';
              end: 'from';
          } => {
        if (
            fromComponentInstance &&
            toComponentInstance &&
            fromComponentInstance.position.x + fromComponentInstance.position.y >
                toComponentInstance.position.x + toComponentInstance.position.y
        ) {
            return {
                start: 'to',
                end: 'from',
            };
        }

        return {
            start: 'from',
            end: 'to',
        };
    },

    getConnectionStartAndEnd: ({
        connection,
        from,
        to,
    }: {
        connection: DiagramConnection;
        from: DiagramComponentInstance;
        to: DiagramComponentInstance;
    }) => {
        const componentInstances = {
            [from.id]: from,
            [to.id]: to,
        };

        const order = ConnectionHelpers.getEndpointOrder(from, to);

        const start = componentInstances[connection[order.start].componentInstanceId];
        const end = componentInstances[connection[order.end].componentInstanceId];

        return { start, end };
    },

    getOrientation: (
        connection: DiagramConnection,
        fromComponentInstance: DiagramComponentInstance,
        toComponentInstance: DiagramComponentInstance,
    ) => {
        ConnectionHelpers.__validateHelperArguments(connection, fromComponentInstance, toComponentInstance);

        const { from, to } = connection;

        const { x: _fromX, y: _fromY } = fromComponentInstance.position;
        const { x: _toX, y: _toY } = toComponentInstance.position;

        const fromXOffset =
            from.edge === 'top' || from.edge === 'bottom'
                ? from.offset
                : from.edge === 'right'
                  ? fromComponentInstance.colSpan - 1
                  : 0;
        const fromYOffset =
            from.edge === 'left' || from.edge === 'right'
                ? from.offset
                : from.edge === 'bottom'
                  ? fromComponentInstance.rowSpan - 1
                  : 0;

        const toXOffset =
            to.edge === 'top' || to.edge === 'bottom'
                ? to.offset
                : to.edge === 'right'
                  ? toComponentInstance.colSpan - 1
                  : 0;
        const toYOffset =
            to.edge === 'left' || to.edge === 'right'
                ? to.offset
                : to.edge === 'bottom'
                  ? toComponentInstance.rowSpan - 1
                  : 0;

        const fromX = _fromX + fromXOffset;
        const fromY = _fromY + fromYOffset;
        const toX = _toX + toXOffset;
        const toY = _toY + toYOffset;

        if (fromX === toX) {
            if (fromY < toY) {
                return 's';
            }

            if (fromY > toY) {
                return 'n';
            }
        }

        if (fromX < toX) {
            if (fromY === toY) {
                return 'e';
            }

            if (fromY < toY) {
                return 'se';
            }

            if (fromY > toY) {
                return 'ne';
            }
        }

        if (fromX > toX) {
            if (fromY === toY) {
                return 'w';
            }

            if (fromY < toY) {
                return 'sw';
            }

            if (fromY > toY) {
                return 'nw';
            }
        }

        return null;
    },

    getLinesLabel: (connection: DiagramConnection, voltageType: VoltageType | null) => {
        const parts: string[] = [];
        let PE = false;

        if (voltageType === 'AC') {
            const lines = connection.lines.AC;

            if (lines.L1 && lines.L2 && lines.L3) {
                parts.push('L1⋯3');
            } else if (lines.L1 && lines.L2) {
                parts.push('L1⋯2');
            } else if (lines.L1 && lines.L3) {
                parts.push('L1 L3');
            } else if (lines.L2 && lines.L3) {
                parts.push('L2⋯3');
            } else if (lines.L1) {
                parts.push('L1');
            } else if (lines.L2) {
                parts.push('L2');
            } else if (lines.L3) {
                parts.push('L3');
            }

            if (lines.N) {
                parts.push('N');
            }

            if (lines.PE) {
                PE = true;
            }
        }

        if (voltageType === 'DC') {
            const lines = connection.lines.DC;

            if (lines['L+']) {
                parts.push('L+');
            }

            if (lines['L-']) {
                parts.push('L-');
            }

            if (lines.M) {
                parts.push('M');
            }

            if (lines.PE) {
                PE = true;
            }
        }

        return {
            lines: parts.join(' ❘ '),
            PE,
        };
    },

    getVoltageType: (
        connection: DiagramConnection,
        fromComponentInstance: DiagramComponentInstance,
        toComponentInstance: DiagramComponentInstance,
    ) => {
        ConnectionHelpers.__validateHelperArguments(connection, fromComponentInstance, toComponentInstance);

        const { from, to } = connection;

        if (isNumber(from.port) && isNumber(to.port)) {
            const fromVoltageType = fromComponentInstance?.configuration.ports?.[from.port]?.voltageType;
            const toVoltageType = toComponentInstance?.configuration.ports?.[to.port]?.voltageType;

            return fromVoltageType === toVoltageType ? fromVoltageType : null;
        }

        return null;
    },

    /**
     * The nominal voltage the two endpoints agree on, or null when there is none to show.
     *
     * A wire carries no nominal of its own — `connection.voltage` is legacy and no longer
     * maintained — so the nominal is read off the ports at either end. One end declaring it is
     * enough; only a genuine disagreement between the two leaves the wire without a nominal.
     */
    getNominalVoltage: (
        connection: DiagramConnection,
        fromComponentInstance: DiagramComponentInstance,
        toComponentInstance: DiagramComponentInstance,
    ): number | null => {
        ConnectionHelpers.__validateHelperArguments(connection, fromComponentInstance, toComponentInstance);

        const { from, to } = connection;

        const nominals = [
            isNumber(from.port) ? PortHelpers.getNominalVoltage(fromComponentInstance, from.port) : null,
            isNumber(to.port) ? PortHelpers.getNominalVoltage(toComponentInstance, to.port) : null,
        ].filter(isNumber);

        const [nominal] = nominals;

        return nominals.every((value) => value === nominal) ? (nominal ?? null) : null;
    },

    getVoltageClass: (
        connection: DiagramConnection,
        voltageType: VoltageType | null,
        voltageClasses: Project['voltageClasses'],
    ): VoltageClass | null => {
        if (voltageType === null) {
            return null;
        }

        const voltage = connection.requirements.voltage.value ?? 0;

        if (voltage === 0) {
            return null;
        }

        return (
            voltageClasses[voltageType].findLast((voltageClass) => {
                return voltage >= voltageClass.min && voltage <= voltageClass.max;
            }) || null
        );
    },

    getFallbackVoltageClassColor: (voltageType: VoltageType | null, fallback = '#868e96') => {
        return voltageType
            ? {
                  AC: '#d9480f',
                  DC: '#1864ab',
              }[voltageType]
            : fallback;
    },

    getNumberOfActiveConductors: (connection: DiagramConnection, voltageType: VoltageType | null) => {
        const { lines } = connection;

        let numberOfConductors = 0;

        if (voltageType === 'DC') {
            numberOfConductors = [lines.DC['L+'], lines.DC['L-'], lines.DC.M].filter(Boolean).length;
        } else if (voltageType === 'AC') {
            numberOfConductors = [lines.AC.L1, lines.AC.L2, lines.AC.L3, lines.AC.N].filter(Boolean).length;
        }

        if (numberOfConductors === 1) {
            // Always at least 2 conductors are required to circulate current
            return 2;
        }

        return numberOfConductors;
    },

    getPathLength: ({
        fromComponentInstance,
        fromAnchor,
        toComponentInstance,
        toAnchor,
    }: {
        fromComponentInstance: DiagramComponentInstance;
        fromAnchor: DiagramConnectionAnchor;
        toComponentInstance: DiagramComponentInstance;
        toAnchor: DiagramConnectionAnchor;
    }) => {
        const getAnchorPoint = (componentInstance: DiagramComponentInstance, anchor: DiagramConnectionAnchor) => {
            const { edge, offset } = anchor;

            const xOffset =
                edge === 'top' || edge === 'bottom' ? offset : edge === 'right' ? componentInstance.colSpan - 1 : 0;
            const yOffset =
                edge === 'left' || edge === 'right' ? offset : edge === 'bottom' ? componentInstance.rowSpan - 1 : 0;

            return {
                x: componentInstance.position.x + xOffset,
                y: componentInstance.position.y + yOffset,
            };
        };

        const fromPoint = getAnchorPoint(fromComponentInstance, fromAnchor);
        const toPoint = getAnchorPoint(toComponentInstance, toAnchor);

        return Math.abs(toPoint.x - fromPoint.x) + Math.abs(toPoint.y - fromPoint.y);
    },

    calculateResistance: (material: 'aluminum' | 'copper', wireSize: number, length: number) => {
        const resistivity = material === 'aluminum' ? 2.65e-8 : 1.68e-8;

        return +((resistivity * length) / (wireSize * 1e-6)).toFixed(4);
    },

    calculateResistanceFromResistancePerLength: (length: number, resistancePerLength: number) => {
        return +(resistancePerLength * length).toFixed(4);
    },

    calculateInductance: (length: number) => {
        return +(0.2e-6 * length).toFixed(10);
    },

    getConnectionForComponentInstance: (
        diagram: Diagram,
        componentInstanceId: DiagramComponentInstance['id'],
        port: DiagramConnection['from' | 'to']['port'],
    ): DiagramConnection | undefined => {
        const { connections } = diagram;

        const isConnection = (connection: DiagramConnection) => {
            return (
                (connection.from.componentInstanceId === componentInstanceId && connection.from.port === port) ||
                (connection.to.componentInstanceId === componentInstanceId && connection.to.port === port)
            );
        };

        return Object.values(connections).find((connection) => isConnection(connection));
    },

    getOtherEndpoint: (
        diagram: Diagram,
        componentInstanceId: DiagramComponentInstance['id'],
        port: DiagramConnection['from' | 'to']['port'],
    ): DiagramConnection['from' | 'to'] | undefined => {
        const connection = ConnectionHelpers.getConnectionForComponentInstance(diagram, componentInstanceId, port);

        if (!connection) {
            return undefined;
        }

        const { from, to } = connection;

        return from.componentInstanceId === componentInstanceId && from.port === port ? to : from;
    },

    generateDefaultValues: (connection: DiagramConnection): Partial<ComponentQuery> => {
        const fromComponentInstance = ComponentInstanceService.getById(connection.from.componentInstanceId);
        const toComponentInstance = ComponentInstanceService.getById(connection.to.componentInstanceId);

        const voltageType = ConnectionHelpers.getVoltageType(connection, fromComponentInstance, toComponentInstance);

        return {
            type: 'cable',
            cable: {
                voltageType: voltageType ?? undefined,
                lines: connection.lines,
                voltage: {
                    max: connection.requirements.voltage.value ?? undefined,
                },
                wireSize: connection.wireSize,
            },
        };
    },

    formatWireSize: (connection: DiagramConnection) => {
        const measurementSystem = MeasurementSystemService.get();
        const parts: string[] = [];

        parts.push(FormatHelpers.formatWireSize(connection.wireSize, measurementSystem));

        if (connection.length.value) {
            parts.push(FormatHelpers.formatLength(connection.length.value, measurementSystem));
        }

        if (connection.multiple.parallel > 1) {
            return `${connection.multiple.parallel}x ${parts.join(' / ')}`;
        }

        return parts.join(' / ');
    },

    isIOConnection: (
        connection: DiagramConnection,
        fromComponentInstance: DiagramComponentInstance,
        toComponentInstance: DiagramComponentInstance,
    ) => {
        return fromComponentInstance.componentType === 'connector' || toComponentInstance.componentType === 'connector';
    },

    getNumberOfConductors: (connection: DiagramConnection, voltageType: VoltageType | null) => {
        const { lines } = connection;

        if (voltageType) {
            return Object.values(lines[voltageType]).filter(Boolean).length;
        }

        return 0;
    },
};

export { ConnectionHelpers };
